import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { MessageCircle, ChevronDown, Link2, Copy } from "lucide-react";
import { whatsappUrl } from "@/components/WhatsAppButton";
import { toast } from "sonner";

/**
 * WhatsAppShortcuts — one-tap templated WhatsApp sends for Marsh Clothing.
 * Includes the 3 top-selling catalog links + a custom-link composer.
 *
 * Props:
 *  - phone (string)     - target phone (with or without country code)
 *  - name (string)      - customer/lead name for salutation
 *  - variant ("full" | "compact")   - "compact" collapses under a single WhatsApp button
 *  - testid (string)
 */
export const CATALOG_LINKS = [
  {
    key: "custom_apparel",
    label: "Premium Custom Apparel",
    url: "https://marshclothing.com/pages/premium-custom-apparel-corporate-uniforms",
    tagline: "Corporate uniforms & premium apparel"
  },
  {
    key: "round_neck",
    label: "Round-neck T-shirts",
    url: "https://marshclothing.com/collections/round-neck-tshirts-custom-printed",
    tagline: "Custom-printed round-neck tees"
  },
  {
    key: "polo",
    label: "Polo T-shirts",
    url: "https://marshclothing.com/collections/polo-tshirts",
    tagline: "Corporate polo tees"
  },
];

// Prebuilt message templates ({name} placeholder replaced at send time)
export const MESSAGE_TEMPLATES = [
  { key: "hello",     label: "Say hello",             body: "Hi {name}, this is Marsh Clothing 👋 How can we help today?" },
  { key: "followup",  label: "Follow-up nudge",       body: "Hi {name}, just following up on your custom apparel enquiry. Should I share our catalog and starting prices?" },
  { key: "quote",     label: "Quote ready",           body: "Hi {name}, your Marsh Clothing quote is ready. Shall we proceed with the order?" },
  { key: "advance",   label: "Advance reminder",      body: "Hi {name}, gentle reminder — please share the 50% advance to lock production slots for your order. UPI: marsh@upi" },
  { key: "delivery",  label: "Delivery today",        body: "Hi {name}, your Marsh Clothing order is out for delivery today. Please keep the balance ready 🙏" },
  { key: "sample",    label: "Sample DC reminder",    body: "Hi {name}, gentle reminder to return the sample DC we shared, or confirm the order so we can convert it to production." },
  { key: "60day",     label: "60-day re-engage",      body: "Hi {name}, it's been a while! Any custom apparel needs coming up? We'd love to work with you again." },
  { key: "birthday",  label: "Festive greeting",      body: "Hi {name}, wishing you a wonderful festive season from Marsh Clothing! 🎉 Any group orders in mind this month?" },
];

function makeMsg(tpl, name) {
  return String(tpl || "").replaceAll("{name}", name || "there");
}

export default function WhatsAppShortcuts({ phone, name = "", variant = "full", testid }) {
  const [open, setOpen] = useState(false);
  const [customUrl, setCustomUrl] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [menuPos, setMenuPos] = useState(null); // {top, right} in viewport coords
  const btnRef = useRef(null);

  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const width = 320; // matches w-80
      const spaceBelow = window.innerHeight - r.bottom;
      const menuHeight = 360; // approx
      const openUp = spaceBelow < menuHeight && r.top > menuHeight;
      const right = Math.max(8, Math.min(window.innerWidth - r.right, window.innerWidth - width - 8));
      const top = openUp ? Math.max(8, r.top - menuHeight - 6) : r.bottom + 6;
      setMenuPos({ top, right });
    }
    setOpen(true);
  };

  // Recompute position on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = 320;
      const spaceBelow = window.innerHeight - r.bottom;
      const menuHeight = 360;
      const openUp = spaceBelow < menuHeight && r.top > menuHeight;
      const right = Math.max(8, Math.min(window.innerWidth - r.right, window.innerWidth - width - 8));
      const top = openUp ? Math.max(8, r.top - menuHeight - 6) : r.bottom + 6;
      setMenuPos({ top, right });
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => { window.removeEventListener("scroll", update, true); window.removeEventListener("resize", update); };
  }, [open]);

  const openWA = (msg) => {
    const url = whatsappUrl(phone, msg);
    if (!url) { toast.error("No valid phone on this record"); return; }
    window.open(url, "_blank", "noopener");
  };

  const sendCatalog = (item) => {
    const msg = `Hi ${name || "there"}, here's our ${item.label.toLowerCase()} range — ${item.tagline}:\n${item.url}\n\nHappy to help you customise. — Marsh Clothing`;
    openWA(msg);
  };

  const sendCustom = () => {
    if (!customUrl) { toast.error("Paste a link first"); return; }
    const msg = customNote
      ? `Hi ${name || "there"}, ${customNote}\n${customUrl}\n\n— Marsh Clothing`
      : `Hi ${name || "there"}, sharing this for your reference:\n${customUrl}\n\n— Marsh Clothing`;
    openWA(msg);
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied"); };

  if (!phone) return null;

  const menuNode = open && menuPos && ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 z-[70]" onClick={() => setOpen(false)} />
      <div
        className="fixed w-80 max-w-[calc(100vw-1rem)] bg-white border border-[#EAE5DB] rounded-lg shadow-2xl z-[80] p-3 space-y-3 text-left"
        style={{ top: menuPos.top, right: menuPos.right, maxHeight: "min(360px, calc(100vh - 20px))", overflowY: "auto" }}
        data-testid="wa-shortcuts-menu"
        onClick={e => e.stopPropagation()}
      >
        <div>
          <div className="label-tiny mb-1.5">Send catalog</div>
          <div className="space-y-1">
            {CATALOG_LINKS.map(item => (
              <div key={item.key} className="flex items-center justify-between gap-2 p-2 rounded hover:bg-[#F5F1E7]">
                <button type="button" className="text-left flex-1 min-w-0" onClick={() => { sendCatalog(item); setOpen(false); }} data-testid={`wa-catalog-${item.key}`}>
                  <div className="text-xs font-medium">{item.label}</div>
                  <div className="text-[10px] text-[#75706A] truncate">{item.tagline}</div>
                </button>
                <button type="button" className="p-1 text-[#75706A] hover:text-[#211F1D]" onClick={() => copy(item.url)} title="Copy link"><Copy size={12}/></button>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-[#EAE5DB] pt-2">
          <div className="label-tiny mb-1.5">Templates</div>
          <div className="grid grid-cols-2 gap-1">
            {MESSAGE_TEMPLATES.map(t => (
              <button
                key={t.key}
                type="button"
                className="text-xs text-left p-1.5 rounded hover:bg-[#F5F1E7] border border-transparent hover:border-[#EAE5DB]"
                onClick={() => { openWA(makeMsg(t.body, name)); setOpen(false); }}
                data-testid={`wa-tpl-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-[#EAE5DB] pt-2 space-y-1.5">
          <div className="label-tiny flex items-center gap-1"><Link2 size={11}/> Custom link + note</div>
          <input
            className="input-field h-8 text-xs"
            placeholder="Paste any URL"
            value={customUrl}
            onChange={e => setCustomUrl(e.target.value)}
            data-testid="wa-custom-url"
          />
          <input
            className="input-field h-8 text-xs"
            placeholder="Optional note above the link"
            value={customNote}
            onChange={e => setCustomNote(e.target.value)}
          />
          <button
            type="button"
            className="btn-primary w-full text-xs"
            onClick={() => { sendCustom(); setOpen(false); }}
            data-testid="wa-custom-send"
          >
            Send on WhatsApp
          </button>
        </div>
      </div>
    </>,
    document.body
  );

  return (
    <div className="inline-block" data-testid={testid || "wa-shortcuts"}>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : openMenu(); }}
        className={`inline-flex items-center gap-1.5 rounded-full font-medium bg-[#25D366] text-white hover:bg-[#1EBE5A] transition-colors ${variant==="compact"?"px-2.5 py-1 text-xs":"px-3.5 py-1.5 text-xs"}`}
        style={{ fontFamily: "Manrope, sans-serif" }}
        data-testid={`${testid || "wa-shortcuts"}-toggle`}
      >
        <MessageCircle size={variant==="compact"?11:13} />
        WhatsApp <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {menuNode}
    </div>
  );
}
